export type Color = {
    name: string | null;
    css: string | null; // CSS variable reference or CSS color string
    hex: string | null; // Hex value (uppercase/lowercase preserved as provided)
    rgba?: string; // Optional RGBA string where needed (used for COLUMN_COLORS shading)
    className?: string;
};

export type ColorMap = {
    [key: string]: Color;
};

export const COLORS: ColorMap = {
    // hex is used within activity digest emails as
    NONE: { name: null, css: null, hex: null },
    GREY: { name: 'GREY', css: 'var(--color-element-grey)', hex: '#bbbec3' },
    CYAN: { name: 'CYAN', css: 'var(--color-element-cyan)', hex: '#62dbc8' },
    GREEN: { name: 'GREEN', css: 'var(--color-element-green)', hex: '#7cd651' },
    BROWN: { name: 'BROWN', css: 'var(--color-element-brown)', hex: '#d58558' },
    YELLOW: { name: 'YELLOW', css: 'var(--color-element-yellow)', hex: '#ffd14d' },
    ORANGE: { name: 'ORANGE', css: 'var(--color-element-orange)', hex: '#ff8d48' },
    RED: { name: 'RED', css: 'var(--color-element-red)', hex: '#ff5757' },
    PINK: { name: 'PINK', css: 'var(--color-element-pink)', hex: '#ff6ed4' },
    PURPLE: { name: 'PURPLE', css: 'var(--color-element-purple)', hex: '#ad6fff' },
    BLUE: { name: 'BLUE', css: 'var(--color-element-blue)', hex: '#4ebafd' },
    DEEP_BLUE: { name: 'DEEP_BLUE', css: 'var(--color-element-deep-blue)', hex: '#5882f8' },
};

export const ICON_COLORS = {
    ...COLORS,
};

export const LABEL_COLORS = {
    WHITE: { name: 'WHITE', css: 'rgba(255,255,255,1)', hex: '#ffffff' },
    ...COLORS,
};

export const BACKGROUND_COLORS: ColorMap = {
    NONE: { ...COLORS.NONE },
    GREY: { ...COLORS.GREY },
    CYAN: { ...COLORS.CYAN },
    GREEN: { ...COLORS.GREEN },
    BROWN: { ...COLORS.BROWN },
    YELLOW: { ...COLORS.YELLOW },
    ORANGE: { ...COLORS.ORANGE },
    RED: { ...COLORS.RED },
    PINK: { ...COLORS.PINK },
    PURPLE: { ...COLORS.PURPLE },
    BLUE: { ...COLORS.BLUE },
    DEEP_BLUE: { ...COLORS.DEEP_BLUE },
    TRANSPARENT: { name: 'TRANSPARENT', css: 'rgba(0,0,0,0)', hex: '#00000000', className: 'transparent' },
};

export const SKETCH_BACKGROUND_COLORS = {
    WHITE: { name: 'WHITE', css: 'rgba(255,255,255,1)', hex: '#ffffff' },
    CHARCOAL: { name: 'CHARCOAL', css: 'var(--color-element-bg-charcoal)', hex: '#5f6672' },
    GREY: { ...BACKGROUND_COLORS.GREY },
    CYAN: { ...COLORS.CYAN },
    GREEN: { ...COLORS.GREEN },
    BROWN: { ...COLORS.BROWN },
    YELLOW: { ...COLORS.YELLOW },
    ORANGE: { ...COLORS.ORANGE },
    RED: { ...COLORS.RED },
    PINK: { ...COLORS.PINK },
    PURPLE: { ...COLORS.PURPLE },
    BLUE: { ...COLORS.BLUE },
    DEEP_BLUE: { ...COLORS.DEEP_BLUE },
};

export const COLUMN_COLORS = {
    NONE: { ...COLORS.NONE },
    GREY: {
        ...COLORS.GREY,
        hex: '#dcdcdc',
        rgba: 'rgba(220, 220, 220, 1)',
        css: 'var(--element-elementlightgrey-900)',
    },
    CYAN: {
        ...COLORS.CYAN,
        hex: '#7be6d5',
        rgba: 'rgba(123, 230, 213, 1)',
        css: 'var(--element-elementteal-900)',
    },
    GREEN: {
        ...COLORS.GREEN,
        hex: '#92e36b',
        rgba: 'rgba(146, 227, 107, 1)',
        css: 'var(--element-elementgreen-900)',
    },
    YELLOW: {
        ...COLORS.YELLOW,
        hex: '#ffd96a',
        rgba: 'rgba(255, 217, 106, 1)',
        css: 'var(--element-elementyellow-900)',
    },
    ORANGE: {
        ...COLORS.ORANGE,
        hex: '#ffa954',
        rgba: 'rgba(255, 169, 84, 1)',
        css: 'var(--element-elementorange-900)',
    },
    RED: {
        ...COLORS.RED,
        hex: '#ff7171',
        rgba: 'rgba(255, 113, 113, 1)',
        css: 'var(--element-elementred-900)',
    },
    PINK: {
        ...COLORS.PINK,
        hex: '#ff8add',
        rgba: 'rgba(255, 138, 221, 1)',
        css: 'var(--element-elementpink-900)',
    },
    PURPLE: {
        ...COLORS.PURPLE,
        hex: '#bf8eff',
        rgba: 'rgba(191, 142, 255, 1)',
        css: 'var(--element-elementpurple-900)',
    },
    BLUE: {
        ...COLORS.BLUE,
        hex: '#6cc7ff',
        rgba: 'rgba(108, 199, 255, 1)',
        css: 'var(--element-elementblue-900)',
    },
    DEEP_BLUE: {
        ...COLORS.DEEP_BLUE,
        hex: '#799bf9',
        rgba: 'rgba(121, 155, 249, 1)',
        css: 'var(--element-elementroyalblue-900)',
    },
};

export const DEFAULT_LABEL_COLORS = Object.values(LABEL_COLORS).filter((color) => color.name !== COLORS.NONE.name);

export const DEFAULT_BOARD_COLORS_ARRAY = Object.values(COLORS)
    .filter((color) => color.name !== COLORS.NONE.name)
    .map((color) => color.name);

export const DEFAULT_BOARD_COLORS_HEX_ARRAY = Object.values(COLORS)
    .filter((color) => color.hex !== COLORS.NONE.hex)
    .map((color) => color.hex);

export const SKETCH_LINE_COLORS: ColorMap = {
    ...COLORS,
    WHITE: { name: 'WHITE', css: 'rgba(255,255,255,1)', hex: '#ffffff' },
};

export const DRAWING_COLORS = {
    ...SKETCH_LINE_COLORS,
};

export const SKETCH_LINE_COLORS_ARRAY = Object.values(SKETCH_LINE_COLORS);

export const ELEMENT_BACKGROUND_COLOR_HEX_VALUES = {
    BLACK: { light: '#323b4a', dark: '#cccccc' },
    CHARCOAL: { light: '#5f6672', dark: '#4e4e4e' },
    SILVER: { light: '#bbbec3', dark: '#808080' },
};

export const DEFAULT_BOARD_COLOR = ELEMENT_BACKGROUND_COLOR_HEX_VALUES.SILVER.light;
export const DEFAULT_BOARD_COLOR_DARK = ELEMENT_BACKGROUND_COLOR_HEX_VALUES.SILVER.dark;
export const DEFAULT_LINE_COLOR = ELEMENT_BACKGROUND_COLOR_HEX_VALUES.BLACK.light;
export const DEFAULT_LINE_COLOR_DARK = ELEMENT_BACKGROUND_COLOR_HEX_VALUES.BLACK.dark;
export const DEFAULT_ELEMENT_COLOR = '#ffffff';

export const CANVAS_COLORS = {
    NONE: { name: null, css: null, displayName: 'Grey' },
    BLUE: { name: 'BLUE', css: 'var(--color-canvas-blue)' },
    GREEN: { name: 'GREEN', css: 'var(--color-canvas-green)' },
    YELLOW: { name: 'YELLOW', css: 'var(--color-canvas-yellow)' },
    WARM_GREY: { name: 'WARM_GREY', css: 'var(--color-canvas-warm-grey)' },
};

export const COLOR_SPACE = {
    HEX: 'HEX',
    RGB: 'RGB',
    HSL: 'HSL',
};

// Used by color swatch to indicate format of color values displayed
export const COLOR_DISPLAY = {
    ...COLOR_SPACE,
    OFF: 'Off',
};

export const TEXT_COLOUR_DARK = '#3d4554';
export const TEXT_COLOUR_LIGHT = '#ffffff';

export const ELEMENT_DEFAULT_BACKGROUND_LIGHT = '#ffffff';
// Mirrors --color-bg-element-primary in dark mode (--color-gray-dark-mode-900).
export const ELEMENT_DEFAULT_BACKGROUND_DARK = '#282828';

export const MENU_BACKGROUND_LIGHT = '#ffffff';
export const MENU_BACKGROUND_DARK = '#282828';
